import { FundingRequestStatus } from '../../interfaces/bbc'
import { ClientInfoStatus, SalesforceStage } from '../../interfaces/client'
import {
  DUE_DILIGENCE_DOCUMENT_REQUEST_STATUS_OPTIONS,
  DueDiligenceDocumentRequestStatus,
  DueDiligenceReportingStatus,
} from '../../interfaces/dueDiligence'
import { FilterFieldType, IFilter } from './types'

/**
 * "Days in diligence" as a SQL expression, computed from the local
 * company_stage_history mirror of Salesforce OpportunityHistory (replacing the old
 * live-SF `calculateAge`). Mirrors calculateAge's logic:
 *   start = earliest company_stage_history.system_mod_stamp where stage = 'Due Diligence'
 *   end   = the client's first funding (earliest SENT borrowing base), else the
 *           latest Closed-Lost stamp, else now
 *   age   = GREATEST(0, days(end - start)); 0 when there is no diligence history yet.
 *
 * Both subqueries correlate on `opportunity_id`, so lead-phase rows (which leave it
 * NULL) are excluded by construction — no `phase` filter needed.
 *
 * `ddInfoAlias` must expose `client_name`; `opportunityAlias` must expose `id`.
 * Shared by the DD list filter/sort dbField, List.ts select, and LoadProspects'
 * avgDaysInDiligence so they never drift.
 */
export const diligenceAgeSql = (ddInfoAlias: string, opportunityAlias: string): string =>
  `GREATEST(0, DATE_PART('day',
    COALESCE(
      (SELECT MIN(bb.record_date)::timestamp FROM borrowing_bases bb
         WHERE bb.client_name = ${ddInfoAlias}.client_name AND bb.status = '${FundingRequestStatus.Sent}'),
      (SELECT MAX(csh_lost.system_mod_stamp) FROM company_stage_history csh_lost
         WHERE csh_lost.opportunity_id = ${opportunityAlias}.id AND csh_lost.stage = '${SalesforceStage.ClosedLost}'),
      NOW()
    )
    - (SELECT MIN(csh_dd.system_mod_stamp) FROM company_stage_history csh_dd
         WHERE csh_dd.opportunity_id = ${opportunityAlias}.id AND csh_dd.stage = '${SalesforceStage.DueDiligence}')
  ))::int`

export const DD_LIST_FILTERS_CONFIG = [
  {
    field: 'clients',
    dbField: 'client_name',
    type: FilterFieldType.Autocomplete,
    isSortable: true,
    placeholder: 'Prospect',
    title: 'Prospect',
    alias: 'clientInfo',
    align: 'left',
  },
  {
    field: 'clientStatus',
    dbField: 'client_status',
    alias: 'clientInfo',
    isMultiple: true,
    type: FilterFieldType.List,
    title: 'Status',
    options: [
      { value: ClientInfoStatus.DueDiligence, label: 'Due Diligence' },
      { value: ClientInfoStatus.Archived, label: 'Archived' },
      { value: ClientInfoStatus.Current, label: 'Current' },
    ],
    align: 'left',
    excludeColumn: true,
  },
  {
    // close_date now comes from the latest local opportunity joined into the DD
    // list (salesforce_dd_info was dropped); alias points the filter/sort at it.
    field: 'closeDate',
    dbField: 'close_date',
    alias: 'opportunity',
    type: FilterFieldType.Date,
    isSortable: true,
    placeholder: 'Target funding date',
    title: 'Target Funding',
    isExternalField: true,
  },
  {
    // "days in diligence" is a computed SQL expression over company_stage_history
    // (see diligenceAgeSql above) rather than a stored column. Aliases match
    // List.ts (dd_info root, opportunity subquery which must select id).
    field: 'age',
    dbField: diligenceAgeSql('dd_info', 'opportunity'),
    type: FilterFieldType.Number,
    isSortable: true,
    isMultiple: true,
    placeholder: 'Age',
    title: 'Age (days)',
  },
  {
    field: 'progress',
    dbField:
      'ROUND(((ddClientInfo.companyBackgroundProgress + ddClientInfo.teamProgress + ddClientInfo.financialCollateralProgress)/300)::numeric,2)',
    type: FilterFieldType.Percent,
    isSortable: true,
    title: 'Progress',
  },
  {
    field: 'riskRating',
    dbField: 'risk_rating',
    alias: 'risk',
    type: FilterFieldType.Number,
    isSortable: true,
    title: 'Risk',
  },
  {
    field: 'ebitda',
    alias: 'financials',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'EBITDA (YTD)',
  },
  {
    field: 'netRevenue',
    dbField: 'net_revenue',
    alias: 'financials',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Net Revenue (YTD)',
  },
  {
    field: 'commitmentAmount',
    dbField: 'commitment_amount',
    alias: 'opportunity',
    type: FilterFieldType.Amount,
    isSortable: true,
    title: 'Commitment Amount',
  },
  {
    field: 'links',
    type: FilterFieldType.Empty,
    title: 'Links',
    align: 'right',
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Due Diligence',
        filters: {
          clientStatus: [ClientInfoStatus.DueDiligence],
        },
      },
    ],
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_LIST_FILTERS_CONFIG = [
  {
    field: 'type',
    type: FilterFieldType.Text,
    dbField: 'ddReporting.type',
    isSortable: true,
    title: 'Report type',
  },
  {
    field: 'boxLink',
    type: FilterFieldType.Empty,
    title: 'Folder',
    align: 'left',
    isSortable: false,
    isCustom: true,
  },
  {
    field: 'createdAt',
    dbField: 'created_at',
    alias: 'ddReporting',
    type: FilterFieldType.Date,
    title: 'Submitted',
    align: 'left',
    isSortable: true,
  },
  {
    field: 'monthsProcessed',
    type: FilterFieldType.Empty,
    align: 'left',
    isSortable: false,
    title: 'Months processed',
  },
  {
    field: 'status',
    dbField: 'status',
    type: FilterFieldType.List,
    isMultiple: true,
    isSortable: true,
    title: 'Status',
    options: [
      {
        value: DueDiligenceReportingStatus.NotStarted,
        label: 'Not started',
      },
      {
        value: DueDiligenceReportingStatus.InProgress,
        label: 'In progress',
      },
      {
        value: DueDiligenceReportingStatus.Completed,
        label: 'Completed',
      },
    ],
  },
  {
    field: 'quickFilterType',
    type: FilterFieldType.QuickFilter,
    fields: [
      {
        field: 'status',
        type: FilterFieldType.List,
      },
    ],
    quickFilters: [
      {
        title: 'All',
        filters: {},
        isHidden: false,
      },
      {
        title: 'Action Required',
        filters: {
          status: ['In progress', 'Not started'],
        },
        isHidden: false,
      },
    ],
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_TEAM_CONTACTS_LIST_FILTERS_CONFIG = [
  {
    field: 'fullName',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Full name',
  },
  {
    field: 'title',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Title',
  },
  {
    field: 'phone',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Phone number',
  },
  {
    field: 'email',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Email',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_TEAM_EXECUTIVES_LIST_FILTERS_CONFIG = [
  {
    field: 'title',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Title',
  },
  {
    field: 'fullName',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Full name',
  },
  {
    field: 'email',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Email',
  },
  {
    field: 'birthday',
    type: FilterFieldType.Date,
    isSortable: true,
    title: 'Date of birth',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_TEAM_BOARD_MEMBERS_LIST_FILTERS_CONFIG = [
  {
    field: 'fullName',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Full name',
  },
  {
    field: 'email',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Email',
  },
  {
    field: 'company',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Company',
  },
  {
    field: 'relationshipToCompany',
    dbField: 'relationship_to_company',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Relationship to company',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_TEAM_REFERENCES_LIST_FILTERS_CONFIG = [
  {
    field: 'fullName',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Full name',
  },
  {
    field: 'businessName',
    dbField: 'business_name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Business name',
  },
  {
    field: 'email',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Email',
  },
  {
    field: 'phone',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Phone number',
  },
  {
    field: 'relationshipToCompany',
    dbField: 'relationship_to_company',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Relationship to company',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_FINANCIALS_INVENTORY_LOCATIONS_LIST_FILTERS_CONFIG = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Name',
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Location Type',
  },
  {
    field: 'landlord',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Landlord',
  },
  {
    field: 'address',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Address',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_FINANCIALS_BANK_ACCOUNTS_LIST_FILTERS_CONFIG = [
  {
    field: 'bankName',
    dbField: 'bank_name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Bank Name',
  },
  {
    field: 'accountHolderName',
    dbField: 'account_holder_name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Account Holder',
  },
  {
    field: 'bankAccountNumber',
    dbField: 'bank_account_number',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Bank Account #',
  },
  {
    field: 'abaRoutingNumber',
    dbField: 'aba_routing_number',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Wire Routing #',
  },
  {
    field: 'purpose',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Purpose',
  },
  {
    field: 'action',
    type: FilterFieldType.Empty,
    title: 'Action',
    align: 'right',
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_TEAM_EXECUTIVES_APPLICATION_LIST_FILTERS_CONFIG = [
  {
    field: 'fullName',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Full name',
  },
  {
    field: 'title',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Title',
  },
  {
    field: 'phone',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Phone number',
  },
  {
    field: 'email',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Email',
  },
  {
    field: 'isBoardMember',
    dbField: 'is_board_member',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Board member',
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_DOCUMENT_REQUEST_LIST_FILTERS_CONFIG = [
  {
    field: 'status',
    alias: 'documentRequest',
    type: FilterFieldType.List,
    isMultiple: true,
    options: DUE_DILIGENCE_DOCUMENT_REQUEST_STATUS_OPTIONS,
  },
  {
    type: FilterFieldType.QuickFilter,
    field: 'quickFilter',
    quickFilters: [
      {
        title: 'Open Requests',
        filters: {
          status: [
            DueDiligenceDocumentRequestStatus.NotStarted,
            DueDiligenceDocumentRequestStatus.InReview,
            DueDiligenceDocumentRequestStatus.ActionRequired,
          ],
        },
      },
      {
        title: 'Complete Requests',
        filters: { status: [DueDiligenceDocumentRequestStatus.Completed] },
      },
    ],
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    alias: 'type',
    excludeColumn: true,
  },
  {
    field: 'name',
    type: FilterFieldType.Text,
    alias: 'type',
    excludeColumn: true,
  },
  {
    field: 'step',
    type: FilterFieldType.Text,
    alias: 'type',
    excludeColumn: true,
  },
  {
    field: 'sub_step',
    type: FilterFieldType.Text,
    alias: 'type',
    excludeColumn: true,
  },
] as const satisfies IFilter[]

export const DUE_DILIGENCE_FINANCIALS_INVENTORY_LOCATIONS_APPLICATION_LIST_FILTERS_CONFIG = [
  {
    field: 'name',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Name',
  },
  {
    field: 'type',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Location Type',
  },
  {
    field: 'totalInventoryAmount',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Total inventory value',
    dbField: 'total_inventory_amount',
  },
  {
    field: 'finishedGoodsAmount',
    type: FilterFieldType.Text,
    isSortable: true,
    title: 'Finished goods value',
    dbField: 'finished_goods_amount',
  },
] as const satisfies IFilter[]

export const DILIGENCE_DOCUMENTS_FILTERS_CONFIG = [
  {
    field: 'nameText',
    dbField: 'name',
    title: 'Document Type',
    type: FilterFieldType.Text,
    align: 'left',
    isSortable: true,
    mobileRender: 'mainText',
  },
  {
    field: 'typeText',
    dbField: 'step',
    title: 'Category',
    type: FilterFieldType.Text,
    align: 'left',
    isSortable: true,
    mobileRender: 'secondaryText',
  },
  {
    field: 'statusText',
    dbField: `(
      CASE 
        WHEN "documentRequest"."status" = '${DueDiligenceDocumentRequestStatus.ActionRequired}' THEN 1
        WHEN "documentRequest"."status" = '${DueDiligenceDocumentRequestStatus.NotStarted}' THEN 2
        WHEN "documentRequest"."status" = '${DueDiligenceDocumentRequestStatus.InReview}' THEN 3
        WHEN "documentRequest"."status" = '${DueDiligenceDocumentRequestStatus.Completed}' THEN 4
        WHEN "documentRequest"."status" = '${DueDiligenceDocumentRequestStatus.Waived}' THEN 5
        ELSE 6
      END
    )`,
    title: 'Status',
    type: FilterFieldType.Text,
    align: 'center',
    isSortable: true,
  },
  {
    field: 'actions',
    title: 'Actions',
    type: FilterFieldType.Empty,
    align: 'center',
  },
  {
    field: 'uploadsAction',
    title: 'Uploads',
    type: FilterFieldType.Empty,
    align: 'right',
  },
  {
    field: 'commentsText',
    title: 'Comments',
    type: FilterFieldType.Empty,
    align: 'left',
  },
] as const satisfies IFilter[]
